import React, { useMemo, useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeft,
  Copy,
  Eye,
  Mail,
  Pencil,
  Save,
  ShieldCheck,
  Trash2,
  User as UserIcon,
} from 'lucide-react';
import api from '../../services/api';
import { useUser, type User } from './useData';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import { Button } from '../../components/ui/Button';
import { Select } from '../../components/ui/Select';
import { Switch } from '../../components/ui/Switch';
import { cn } from '../../lib/utils';
import {
  normalizePermissionRows,
  type PermissionModuleKey,
  type PermissionRow,
} from '../../config/permissionModules';
import UserPermissionsForm, { type AccessLevel, type GroupPreset } from './UserPermissionsForm';
import HelpItems from '../../components/HelpItems/HelpItems';
import { docsArticleUrl } from '../../config/docsConfig';

export interface UserPermissionsPayload {
  permissions: PermissionRow[];
  canAssignEmailsToOthers: boolean;
}

function isPermissionsPayload(data: unknown): data is UserPermissionsPayload {
  return (
    typeof data === 'object' &&
    data != null &&
    Array.isArray((data as UserPermissionsPayload).permissions)
  );
}

function parsePermissionsResponse(data: unknown): UserPermissionsPayload {
  if (isPermissionsPayload(data)) {
    return {
      permissions: normalizePermissionRows(data.permissions),
      canAssignEmailsToOthers: data.canAssignEmailsToOthers === true,
    };
  }
  if (Array.isArray(data)) {
    return {
      permissions: normalizePermissionRows(data),
      canAssignEmailsToOthers: false,
    };
  }
  return { permissions: normalizePermissionRows([]), canAssignEmailsToOthers: false };
}

function userInitials(user: User): string {
  const fromName = `${user.userFirstName?.[0] ?? ''}${user.userLastName?.[0] ?? ''}`.trim();
  if (fromName) return fromName.toUpperCase();
  return (user.userEmail?.[0] ?? '?').toUpperCase();
}

function permissionStats(permissions: PermissionRow[]) {
  return {
    view: permissions.filter((p) => p.canView).length,
    edit: permissions.filter((p) => p.canEdit).length,
    delete: permissions.filter((p) => p.canDelete).length,
    total: permissions.length,
  };
}

function SummaryMeter({
  label,
  value,
  total,
  icon: Icon,
  iconClass,
  barClass,
  trackClass,
}: {
  label: string;
  value: number;
  total: number;
  icon: typeof Eye;
  iconClass: string;
  barClass: string;
  trackClass: string;
}) {
  const { colors } = useTheme();
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={cn('w-3.5 h-3.5 shrink-0', iconClass)} aria-hidden />
          <span className={cn('text-xs font-medium', colors.textMuted)}>{label}</span>
        </div>
        <span className={cn('text-xs font-semibold tabular-nums', colors.text)}>
          {value}
          <span className={cn('font-normal', colors.textMuted)}>/{total}</span>
        </span>
      </div>
      <div className={cn('h-2 rounded-full overflow-hidden', trackClass)}>
        <div className={cn('h-full rounded-full transition-all duration-300', barClass)} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const UserPermissionsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { colors } = useTheme();
  const { i18n } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const userId = id ? parseInt(id, 10) : NaN;
  const { user, isLoading: userLoading } = useUser(userId);
  const [permissions, setPermissions] = useState<PermissionRow[]>([]);
  const [canAssignEmailsToOthers, setCanAssignEmailsToOthers] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [otherUsers, setOtherUsers] = useState<User[]>([]);
  const [copyFromUserId, setCopyFromUserId] = useState<string>('');
  const [copying, setCopying] = useState(false);

  const stats = useMemo(() => permissionStats(permissions), [permissions]);

  useEffect(() => {
    if (!id || Number.isNaN(userId)) return;
    const load = async () => {
      try {
        setLoading(true);
        const res = await api.get<UserPermissionsPayload | PermissionRow[]>(`/users/${userId}/permissions`);
        const parsed = parsePermissionsResponse(res.data);
        setPermissions(parsed.permissions);
        setCanAssignEmailsToOthers(parsed.canAssignEmailsToOthers);
      } catch (e) {
        console.error(e);
        showError('Failed to load permissions');
        setPermissions(normalizePermissionRows([]));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [userId, id, showError]);

  useEffect(() => {
    if (userLoading || !userId) return;
    const loadUsers = async () => {
      try {
        const res = await api.get<User[] | { data: User[] }>('/users');
        const list = Array.isArray(res.data) ? res.data : (res.data as { data: User[] })?.data ?? [];
        setOtherUsers(list.filter((u) => u.id !== userId));
      } catch {
        setOtherUsers([]);
      }
    };
    loadUsers();
  }, [userId, userLoading]);

  const handleCopyFromUser = async () => {
    const sourceId = copyFromUserId ? parseInt(copyFromUserId, 10) : NaN;
    if (Number.isNaN(sourceId)) {
      showError('Select a user to copy from');
      return;
    }
    try {
      setCopying(true);
      const res = await api.get<UserPermissionsPayload | PermissionRow[]>(`/users/${sourceId}/permissions`);
      const parsed = parsePermissionsResponse(res.data);
      setPermissions(parsed.permissions);
      setCanAssignEmailsToOthers(parsed.canAssignEmailsToOthers);
      setCopyFromUserId('');
      showSuccess('Permissions copied. Review and save to apply.');
    } catch (e) {
      console.error(e);
      showError('Failed to load permissions from user');
    } finally {
      setCopying(false);
    }
  };

  const handleChange = (moduleKey: string, field: 'canView' | 'canEdit' | 'canDelete', value: boolean) => {
    setPermissions((prev) =>
      prev.map((p) => {
        if (p.moduleKey !== moduleKey) return p;
        if (field === 'canView' && !value) {
          return { ...p, canView: false, canEdit: false, canDelete: false };
        }
        return { ...p, [field]: value };
      }),
    );
  };

  const handleRowLevel = (moduleKey: string, level: AccessLevel) => {
    setPermissions((prev) =>
      prev.map((p) => {
        if (p.moduleKey !== moduleKey) return p;
        switch (level) {
          case 'none':
            return { ...p, canView: false, canEdit: false, canDelete: false };
          case 'view':
            return { ...p, canView: true, canEdit: false, canDelete: false };
          case 'edit':
            return { ...p, canView: true, canEdit: true, canDelete: false };
          case 'full':
            return { ...p, canView: true, canEdit: true, canDelete: true };
        }
      }),
    );
  };

  const handleGroupPreset = (keys: PermissionModuleKey[], preset: GroupPreset) => {
    const keySet = new Set(keys);
    setPermissions((prev) =>
      prev.map((p) => {
        if (!keySet.has(p.moduleKey as PermissionModuleKey)) return p;
        switch (preset) {
          case 'none':
            return { ...p, canView: false, canEdit: false, canDelete: false };
          case 'view':
            return { ...p, canView: true, canEdit: false, canDelete: false };
          case 'edit':
            return { ...p, canView: true, canEdit: true, canDelete: false };
          case 'full':
            return { ...p, canView: true, canEdit: true, canDelete: true };
        }
      }),
    );
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/users/${userId}/permissions`, {
        permissions: normalizePermissionRows(permissions),
        canAssignEmailsToOthers,
      });
      showSuccess('Permissions saved');
      navigate(-1);
    } catch (e) {
      console.error(e);
      showError('Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  if (userLoading || !user) {
    return (
      <div className={cn('p-6 max-w-7xl mx-auto', colors.text)}>
        <div className="animate-pulse space-y-4">
          <div className={cn('h-16 rounded-2xl', colors.surface, colors.border, 'border')} />
          <div className="grid lg:grid-cols-[300px_1fr] gap-6">
            <div className={cn('h-80 rounded-2xl', colors.surface, colors.border, 'border')} />
            <div className={cn('h-96 rounded-2xl', colors.surface, colors.border, 'border')} />
          </div>
        </div>
      </div>
    );
  }

  if (user.role === 'ADMIN') {
    return (
      <div className={cn('p-6 max-w-2xl mx-auto', colors.text)}>
        <div className={cn('rounded-2xl border p-8 text-center shadow-sm', colors.surface, colors.border)}>
          <div className={cn('mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4', colors.primary, colors.primaryText)}>
            <ShieldCheck className="w-7 h-7" aria-hidden />
          </div>
          <h1 className={cn('text-lg font-semibold mb-2', colors.text)}>Full administrator access</h1>
          <p className={cn('text-sm mb-6', colors.textMuted)}>
            Admin users have unrestricted access to all modules. Individual permissions are not editable.
          </p>
          <Button variant="secondary" onClick={() => navigate(-1)} className="inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" aria-hidden />
            Back
          </Button>
        </div>
      </div>
    );
  }

  const displayName =
    user.userDisplayName || `${user.userFirstName || ''} ${user.userLastName || ''}`.trim() || user.userEmail;

  return (
    <div className={cn('min-h-full', colors.background, colors.text)}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 md:py-6 space-y-6">
        {/* Top bar */}
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={cn('p-2 rounded-xl transition-colors shrink-0', colors.textMuted, colors.interactiveHover)}
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <p className={cn('text-xs font-medium uppercase tracking-wide', colors.textMuted)}>User permissions</p>
            <h1 className={cn('text-xl md:text-2xl font-semibold truncate', colors.text)}>{displayName}</h1>
          </div>
          <div className="hidden sm:flex gap-2 w-full sm:w-auto sm:ml-auto">
            <Button variant="secondary" onClick={() => navigate(-1)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving || loading} loading={saving} className="inline-flex items-center gap-2">
              <Save className="w-4 h-4" aria-hidden />
              Save changes
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-[minmax(260px,300px)_minmax(0,1fr)] gap-6 items-start">
          {/* Sidebar */}
          <aside className="space-y-4 lg:sticky lg:top-4">
            <div className={cn('rounded-2xl border overflow-hidden', colors.border, colors.surface)}>
              <div className={cn('h-16 bg-gradient-to-r from-indigo-500/15 via-violet-500/10 to-transparent border-b', colors.border)} />
              <div className="px-4 pb-4 -mt-8">
                <div className={cn('w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-semibold shadow-md border-2', colors.surface, colors.border, colors.primary, colors.primaryText)}>
                  {userInitials(user)}
                </div>
                <div className="mt-3 space-y-1">
                  <p className={cn('font-semibold', colors.text)}>{displayName}</p>
                  <p className={cn('text-sm truncate', colors.textMuted)}>{user.userEmail}</p>
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    <span
                      className={cn(
                        'text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full',
                        user.userIsActive
                          ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300'
                          : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
                      )}
                    >
                      {user.userIsActive ? 'Active' : 'Inactive'}
                    </span>
                    <span className={cn('text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full', colors.surfaceHover, colors.textMuted)}>
                      {user.role}
                    </span>
                  </div>
                </div>
                <Link
                  to={`/admin/users/${user.id}`}
                  className={cn('inline-flex items-center gap-1.5 text-xs font-medium mt-3', colors.textSecondary, 'hover:underline')}
                >
                  <UserIcon className="w-3.5 h-3.5" aria-hidden />
                  View user profile
                </Link>
              </div>
            </div>

            <div className={cn('rounded-2xl border p-4 space-y-4', colors.border, colors.surface)}>
              <div>
                <p className={cn('text-sm font-semibold', colors.text)}>Access overview</p>
                <p className={cn('text-xs mt-0.5', colors.textMuted)}>
                  {stats.view} of {stats.total} modules visible to this user
                </p>
              </div>
              <SummaryMeter
                label="View"
                value={stats.view}
                total={stats.total}
                icon={Eye}
                iconClass="text-blue-600 dark:text-blue-400"
                barClass="bg-blue-500"
                trackClass={colors.surfaceHover}
              />
              <SummaryMeter
                label="Edit"
                value={stats.edit}
                total={stats.total}
                icon={Pencil}
                iconClass="text-amber-600 dark:text-amber-400"
                barClass="bg-amber-500"
                trackClass={colors.surfaceHover}
              />
              <SummaryMeter
                label="Delete"
                value={stats.delete}
                total={stats.total}
                icon={Trash2}
                iconClass="text-emerald-600 dark:text-emerald-400"
                barClass="bg-emerald-500"
                trackClass={colors.surfaceHover}
              />
            </div>

            <div className={cn('rounded-2xl border p-4', colors.border, colors.surface)}>
              <div className="flex items-start gap-3">
                <div className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-xl', 'bg-rose-500/10 text-rose-600 dark:text-rose-400')}>
                  <Mail className="h-4 w-4" aria-hidden />
                </div>
                <div className="min-w-0 flex-1">
                  <p className={cn('text-sm font-semibold', colors.text)}>Email assignment</p>
                  <p className={cn('text-xs mt-0.5 leading-relaxed', colors.textMuted)}>
                    Allow assigning messages to other users in the email view.
                  </p>
                </div>
                <Switch
                  checked={canAssignEmailsToOthers}
                  onChange={(e) => setCanAssignEmailsToOthers(e.target.checked)}
                  aria-label="Can assign emails to other users"
                />
              </div>
            </div>

            <div className={cn('rounded-2xl border overflow-hidden', colors.border, colors.surface)}>
              <div className="px-4 py-3 border-b border-inherit">
                <div className="flex items-center gap-2">
                  <Copy className={cn('w-4 h-4', colors.textMuted)} aria-hidden />
                  <p className={cn('text-sm font-semibold', colors.text)}>Copy from user</p>
                </div>
                <p className={cn('text-xs mt-1', colors.textMuted)}>Load another user&apos;s permissions, then save.</p>
              </div>
              <div className="p-4 space-y-3">
                {otherUsers.length === 0 ? (
                  <p className={cn('text-sm', colors.textMuted)}>No other users available.</p>
                ) : (
                  <>
                    <Select
                      id="copy-from-user"
                      value={copyFromUserId}
                      onChange={(e) => setCopyFromUserId(e.target.value)}
                      disabled={copying}
                      showClearButton={false}
                      searchable={otherUsers.length > 6}
                      placeholder="Choose a user…"
                      dropdownMinWidth={0}
                      options={otherUsers.map((u) => ({
                        value: String(u.id),
                        label:
                          u.userDisplayName ||
                          `${u.userFirstName || ''} ${u.userLastName || ''}`.trim() ||
                          u.userEmail,
                        description: u.userEmail,
                      }))}
                    />
                    <Button
                      type="button"
                      variant={copyFromUserId ? 'default' : 'secondary'}
                      className="w-full"
                      onClick={handleCopyFromUser}
                      disabled={!copyFromUserId || copying}
                      loading={copying}
                    >
                      {copying ? 'Copying…' : 'Copy permissions'}
                    </Button>
                  </>
                )}
              </div>
            </div>

            <HelpItems
              items={[
                {
                  summary: 'Access levels',
                  content: (
                    <p>
                      <strong>None</strong> blocks the module. <strong>View</strong> opens read-only access.{' '}
                      <strong>Edit</strong> allows create and update. <strong>Full</strong> also allows delete where supported.
                    </p>
                  ),
                  readMoreHref: docsArticleUrl('permissions', i18n.language),
                },
              ]}
            />

            <div className="hidden lg:flex flex-col gap-2">
              <Button onClick={handleSave} disabled={saving || loading} loading={saving} className="w-full inline-flex items-center justify-center gap-2">
                <Save className="w-4 h-4" aria-hidden />
                Save changes
              </Button>
              <Button variant="secondary" className="w-full" onClick={() => navigate(-1)} disabled={saving}>
                Cancel
              </Button>
            </div>
          </aside>

          {/* Main permissions matrix */}
          <main className="min-w-0">
            <UserPermissionsForm
              permissions={permissions}
              onChange={handleChange}
              onRowLevel={handleRowLevel}
              onGroupPreset={handleGroupPreset}
              loading={loading}
            />
          </main>
        </div>
      </div>

      {/* Mobile sticky footer */}
      <div className={cn('sm:hidden sticky bottom-0 border-t px-4 py-3 flex gap-2', colors.border, colors.surface)}>
        <Button variant="secondary" className="flex-1" onClick={() => navigate(-1)} disabled={saving}>
          Cancel
        </Button>
        <Button className="flex-1" onClick={handleSave} disabled={saving || loading} loading={saving}>
          Save
        </Button>
      </div>
    </div>
  );
};

export default UserPermissionsPage;
